import logoFull from '../assets/logo-full.png';
import { useClientConfig } from '../lib/config.js';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ErrorBanner, Loading, Spinner } from '../components/ui.js';
import { authErrorMessage, fetchAuthProviders, signIn, signUp, useSession } from '../lib/auth.js';
import { useAsync } from '../lib/hooks.js';
import { titleCase } from '../lib/format.js';

/**
 * How each provider spells its own name.
 *
 * Title-casing the provider key gets "Linkedin", "Github" and — worse — keeps
 * calling X "Twitter". These are trademarks on a sign-in button, so they are
 * worth spelling correctly. Anything not listed falls back to title case, so a
 * newly configured provider still renders something sensible.
 */
const PROVIDER_NAMES: Record<string, string> = {
  google: 'Google',
  github: 'GitHub',
  facebook: 'Facebook',
  // The provider key stays `twitter` because that is what Better Auth calls it.
  twitter: 'X',
  linkedin: 'LinkedIn',
  microsoft: 'Microsoft',
  apple: 'Apple',
};

const providerName = (provider: string): string => PROVIDER_NAMES[provider] ?? titleCase(provider);

/**
 * Sign in / sign up.
 *
 * The split panel comes from the Antargyan design: the proposition on the teal
 * field, the form on the light side. The panel is decoration and is dropped
 * below 900px rather than stacked above the form.
 *
 * Social buttons render only for providers the server actually has credentials
 * for, which it reports from `/api/auth-providers`. The design shows a full row
 * of them, but rendering a Google button that answers "provider not found"
 * would be worse than showing none.
 */

/*
 * What a teacher gets, in a teacher's words.
 *
 * These four used to read "One place per tenant", "Usage you can see",
 * "Billing that reconciles" and "Plans that hold still" — the vocabulary of the
 * data model and of a billing system that is switched off. Nobody deciding
 * whether to teach a class here was ever going to be persuaded by "metered
 * against the plan".
 */
const FEATURES: ReadonlyArray<{ icon: string; title: string; text: string }> = [
  { icon: '▶', title: 'Zoom or YouTube Live', text: 'Schedule it once, we make the link' },
  { icon: '⧉', title: 'Codes you hand out', text: 'One per student, used once' },
  { icon: '↻', title: 'A whole term at a time', text: 'Weekly, monthly, or alternate days' },
  { icon: '◈', title: 'They join from anywhere', text: 'Windows, Android, or the browser' },
];

export function SignInPage(): ReactNode {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const providers = useAsync(fetchAuthProviders, []);
  const config = useClientConfig();

  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /*
   * Two inputs, one stored value. The dial code is a picker because typing "+91"
   * is the single most common way to get a phone number wrong, and the national
   * number is kept separate so a paste with spaces or hyphens is harmless.
   */
  const [dialCode, setDialCode] = useState('+91');
  const [localNumber, setLocalNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  if (isPending) return <Loading label="Checking your session…" />;
  if (session?.user) return <Navigate to={from} replace />;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const result =
      mode === 'signup'
        ? await signUp.email({
            name,
            email,
            password,
            // E.164. The server normalises and validates this again; composing
            // here is a convenience, not the check.
            phoneNumber: `${dialCode}${localNumber.replace(/[\s\-()./]/g, '')}`,
          })
        : await signIn.email({ email, password });

    setBusy(false);
    if (result.error) {
      setError(
        authErrorMessage(
          result.error,
          mode === 'signup' ? 'Could not create the account.' : 'Could not sign in.',
        ),
      );
      return;
    }
    window.location.href = from;
  }

  return (
    <div className="auth-split">
      <aside className="auth-aside" aria-hidden="true">
        <div className="auth-aside-brand">
          <img src={logoFull} alt="" className="auth-aside-logo" width={130} height={161} />
        </div>

        <div>
          <h1 className="auth-headline">Teach live. Students just turn up.</h1>
          <p className="auth-lede">
            Schedule a class, hand out a code, and your students are in — on a phone, a laptop, or
            whatever they own.
          </p>
        </div>

        <ul className="auth-features">
          {FEATURES.map((feature) => (
            <li key={feature.title} className="auth-feature">
              <span className="auth-feature-icon">{feature.icon}</span>
              <span>
                <span className="auth-feature-title">{feature.title}</span>
                <span className="auth-feature-text" style={{ display: 'block' }}>
                  {feature.text}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </aside>

      <main className="auth-main">
        <div className="auth-form stack">
          <div>
            <h1>{mode === 'signup' ? 'Create your account' : 'Welcome back'}</h1>
            <p className="muted" style={{ marginTop: '0.5rem' }}>
              {mode === 'signup'
                ? 'You can create an organization straight after.'
                : 'Sign in to see your classes'}
            </p>
          </div>

          <ErrorBanner message={error} />

          {providers.data && providers.data.social.length > 0 && (
            <>
              <div className="button-row">
                {providers.data.social.map((provider) => (
                  <button
                    key={provider}
                    type="button"
                    className="button-secondary"
                    style={{ flex: 1 }}
                    onClick={() => {
                      void signIn.social({ provider, callbackURL: from });
                    }}
                  >
                    {providerName(provider)}
                  </button>
                ))}
              </div>
              <div className="divider">or continue with email</div>
            </>
          )}

          <form onSubmit={(e) => void handleSubmit(e)} className="stack">
            {mode === 'signup' && (
              <div>
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                />
              </div>
            )}

            {mode === 'signup' && (
              <div>
                <label htmlFor="phone">Phone number</label>
                <div className="row" style={{ gap: '0.5rem', flexWrap: 'nowrap' }}>
                  <select
                    aria-label="Country dialling code"
                    value={dialCode}
                    onChange={(e) => setDialCode(e.target.value)}
                    style={{ maxWidth: '11rem', flexShrink: 0 }}
                  >
                    {/*
                      From the server, so a correction does not need a deploy of
                      each client. Falls back to the default while it loads
                      rather than rendering an empty picker.
                    */}
                    {(config.dialCodes.length > 0
                      ? config.dialCodes
                      : [{ value: '+91', label: 'India +91' }]
                    ).map((code) => (
                      <option key={code.value} value={code.value}>
                        {code.label}
                      </option>
                    ))}
                  </select>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    value={localNumber}
                    onChange={(e) => setLocalNumber(e.target.value)}
                    placeholder="98765 43210"
                    required
                    autoComplete="tel-national"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                minLength={12}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
              {mode === 'signup' && <p className="field-hint">At least 12 characters.</p>}
            </div>

            <button type="submit" className="button-primary button-block" disabled={busy}>
              {busy && <Spinner />}
              {mode === 'signup' ? 'Create account' : 'Sign In'}
            </button>
          </form>

          {providers.data && providers.data.social.length === 0 && (
            <p className="field-hint">
              Google and GitHub sign-in appear here once their credentials are configured on the
              server.
            </p>
          )}

          <p className="small muted" style={{ textAlign: 'center' }}>
            {mode === 'signup' ? 'Already have an account?' : 'Don’t have an account?'}{' '}
            <button
              type="button"
              className="button-ghost"
              onClick={() => {
                setMode(mode === 'signup' ? 'signin' : 'signup');
                setError(null);
              }}
            >
              {mode === 'signup' ? 'Sign in' : 'Create One'}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
